import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SmsFailedOutlinedIcon from '@mui/icons-material/SmsFailedOutlined';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { StyledDialog } from 'theme/index';
import { ERROR_TEXT_COLOR } from '../SyncCalendar/syncStatusDisplay';

/** MM-1198: props of MessageCell. */
interface MessageCellProps {
  /** The message; '-' is shown when empty. */
  message: string | null;
  /** Title of the dialog with the full message, e.g. "Cause of error". */
  title: string;
  /** Shows the message in the error color. */
  error?: boolean;
  /** Widest the one-line text may get before it is cut with an ellipsis. */
  maxWidth?: number;
  /** MM-1198: for an error, also show a message icon in front of the text that opens the full message. */
  errorIcon?: boolean;
  /**
   * MM-1198: show only an icon (no text) that opens the full message - a red message icon for an error, a grey
   * info icon otherwise.
   */
  iconOnly?: boolean;
}

/**
 * MM-1198: a long message in a table cell - shown on one line, cut with an ellipsis so the row keeps its height;
 * clicking it opens a dialog with the full message (one line per attempt kept). With errorIcon, an error also gets
 * a message icon in front that opens the same dialog; with iconOnly, only that icon is shown (no text).
 */
const MessageCell = ({
  message,
  title,
  error = false,
  maxWidth = 280,
  errorIcon = false,
  iconOnly = false,
}: MessageCellProps) => {
  /** True while the full-message dialog is open. */
  const [open, setOpen] = useState<boolean>(false);
  if (!message) {
    return (
      <Typography variant='body2' color='text.secondary'>
        -
      </Typography>
    );
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {error && (errorIcon || iconOnly) ? (
        <Tooltip title='Show error'>
          <IconButton size='small' onClick={() => setOpen(true)} sx={{ color: ERROR_TEXT_COLOR, p: 0.25 }}>
            <SmsFailedOutlinedIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      ) : null}
      {!error && iconOnly ? (
        <Tooltip title='Show details'>
          <IconButton size='small' onClick={() => setOpen(true)} sx={{ color: 'text.secondary', p: 0.25 }}>
            <InfoOutlinedIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      ) : null}
      {iconOnly ? null : (
        <Link
          component='button'
          type='button'
          underline='hover'
          onClick={() => setOpen(true)}
          title='Show full message'
          sx={{
            display: 'block',
            maxWidth,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textAlign: 'left',
            fontSize: '0.875rem',
            color: error ? ERROR_TEXT_COLOR : 'text.secondary',
          }}
        >
          {message.replace(/\n/g, ' | ')}
        </Link>
      )}
      {/* the platform's common dialog (StyledDialog), laid out like the admin Document Details dialog */}
      <StyledDialog open={open} onClose={() => setOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>{title}</DialogTitle>
        <DialogContent>
          <Typography
            variant='body2'
            sx={{ whiteSpace: 'pre-line', wordBreak: 'break-word', color: error ? ERROR_TEXT_COLOR : 'text.primary' }}
          >
            {message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'center' }}>
          <Button variant='contained' onClick={() => setOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </StyledDialog>
    </Box>
  );
};

export default MessageCell;
